import { Goal } from "@/models/Goal.model";
import { GoalSearchResult } from "@/stores/Goals.store";
import { Curriculum } from "@/models/Curriculum.model.ts";
import { resolveGoalStructureProvider } from "@/goals/utils/GoalStructureProvider";

export interface GoalSourceItemContainer {
	items: GoalSourceContentItem[];
}

export interface GoalSourceContent extends GoalSourceItemContainer {
	copyright: string;
	name: string;
	version: string;
	structure_only?: boolean; // if true, the content only contains the structure of the goal source, no actual goals
}

export interface GoalSourceContentItem extends GoalSourceItemContainer {
	index: number;
	path: string;
	title: string;
	info: string;
	items: GoalSourceContentItem[];
	/**
	 * Source ids of the goals that belong under this node, for curricula whose goal paths
	 * do not spell out the display hierarchy. GO! Navigator numbers its goals per
	 * curriculum ("NL.003") no matter how deep in the tree they sit, so the path cannot say
	 * which heading a goal belongs to — and those numbers are positions, reassigned when
	 * the curriculum gains a goal, so they are no basis for matching either. The source id
	 * is the goal's stable identity. Omitted by curricula whose paths already nest (ZiLL,
	 * SLO, OVSG, Op Stap).
	 */
	goalIds?: string[];
	meta? : {
		color? : string;
	}
}

export class PopulatedGoalStructureContent {
	constructor(
		public sourceId: string | null,
		public name: string = '',
		public version: string = '',
		public copyright: string = '',
		public items: PopulatedGoalStructureContentItem[] = [],
	) {}
}

export class PopulatedGoalStructureContentItem extends Goal {

	public items: (GoalSearchResult | PopulatedGoalStructureContentItem)[];

	public path: string;

	public sourceId: string | null;

	/** See GoalSourceContentItem.goalIds. */
	public goalIds: string[];

	constructor(
		path: string,
		title: string,
		info: string,
		items: (GoalSearchResult | PopulatedGoalStructureContentItem)[],
		sourceId: string | null = null,
		goalIds: string[] = [],
	) {
		super();
		this.path = path;
		this.title = title;
		this.info = info;
		this.items = items;
		this.sourceId = sourceId;
		this.goalIds = goalIds;
	}

	/**
	 * Path prefixed with the source id, so DOM ids and v-for keys stay unique when
	 * goal structures from multiple sources are rendered together.
	 */
	public get uniquePath(): string {
		return (this.sourceId ? this.sourceId + ':' : '') + this.path;
	}

	public get goalsFlat(): GoalSearchResult[] {
		const out: GoalSearchResult[] = [];

		function traverse(node: GoalSearchResult | PopulatedGoalStructureContentItem) {
			if (node instanceof GoalSearchResult) {
				out.push(node);
			} else {
				node.items.forEach(traverse);
			}
		}

		traverse(this);
		return out;
	}
}

export class GoalSource {

	private sourceId: string | null = null;

	/** @warning This is a private property, do not use it directly. Instead, use getEmptyStructure to get a clean copy */
	private emptyStructureJson: string | null = null;

	public content: GoalSourceContent | null = null;

	private loadPromise: Promise<void> | null = null;

	constructor() {}

	async load(curriculum: Curriculum) {

		const sourceId = curriculum.id;
		if (sourceId !== this.sourceId) {
			this.sourceId = sourceId;
			this.loadPromise = null;
		}

		if (this.loadPromise !== null) {
			return this.loadPromise;
		}

		this.loadPromise = new Promise<void>(async (resolve, reject) => {

			try {
				const provider = resolveGoalStructureProvider(curriculum);
				const content = await provider.loadContent(curriculum);

				if (this.sourceId !== sourceId) {
					reject();
					return;
				}

				this.content = content;
				this.emptyStructureJson = content
					? JSON.stringify(this.buildEmptyStructure())
					: null;

				resolve();
			} catch (e) {
				console.error(e);
				this.content = null;
				this.emptyStructureJson = null;
				resolve();
			}

		});
		return this.loadPromise;

	}

	getName() {
		return this.content?.name;
	}

	getVersion() {
		return this.content?.version;
	}

	buildEmptyStructure(): PopulatedGoalStructureContent {

		if (!this.content) {
			return new PopulatedGoalStructureContent(this.sourceId);
		}

		// if(!this.content.items) {
		// 	return {};
		// }
		function traverse(node: GoalSourceItemContainer, structureOnly: boolean = false)
		{

			// If the node has children, create a new object and recursively traverse
			if (node.items && node.items.length > 0) {
				return {
					...node,
					items: node.items.map(
						v => traverse(v, structureOnly) // Recursively traverse children
					).filter(
						(item: any) => item !== undefined
					) // Remove nodes without children
				};
			}

			// If we are collectiong the structure, we still need to return the node
			if (structureOnly) {
				return {
					...node,
					items: []
				};
			}

			// If the node has no children, return undefined to filter it out
			return undefined;
		}

		return traverse(this.content, this.content.structure_only);

	}

	/**
	 * Get a clean copy of the empty structure, without any goals
	 * @returns PopulatedGoalStructureContent
	 */
	getEmptyStructure(): PopulatedGoalStructureContent {
		if (!this.emptyStructureJson) {
			return new PopulatedGoalStructureContent(this.sourceId);
		}

		const sourceId = this.sourceId;

		function mapToPopulatedGoalStructureContentItem(
			items: any[],
			meta: any = {}
		): PopulatedGoalStructureContentItem[] {
			if (typeof(items) === 'undefined') {
				return [];
			}

			return items.map(item => {

				// In order to set meta data, we need to merge the meta data from the parent with the
				// meta data from the child; that way all children know the meta data of their parent
				if (typeof(item.meta) !== 'undefined') {
					meta = Object.assign({}, meta, item.meta);
				}

				const child = new PopulatedGoalStructureContentItem(
					item.path,
					item.title,
					item.info,
					mapToPopulatedGoalStructureContentItem(item.items, meta),
					sourceId,
					item.goalIds ?? []
				);

				if (typeof(meta.color) !== 'undefined') {
					child.color = meta.color;
				}

				return child;
			});
		}

		const json = JSON.parse(this.emptyStructureJson);
		return new PopulatedGoalStructureContent(
			this.sourceId,
			json.name,
			json.version,
			json.copyright,
			mapToPopulatedGoalStructureContentItem(json.items)
		);
	}

	/**
	 * Generate a map of all nodes in the structured goal data, indexed by their path
	 */
	calculateNodePathMap(structuredGoalData: PopulatedGoalStructureContent): Map<string, PopulatedGoalStructureContentItem> {

		const map = new Map<string, PopulatedGoalStructureContentItem>();

		const traverse = (node: PopulatedGoalStructureContentItem | any) => {
			if (node.path) {
				map.set(node.path, node);
			}

			if (node.items && node.items.length > 0) {
				node.items.forEach(traverse);
			}
		}

		structuredGoalData.items.forEach(traverse);
		return map;

	}

	/**
	 * Map of goal source id -> the node that claims it via `goalIds`. Empty for curricula
	 * whose goal paths already nest inside the display hierarchy.
	 */
	calculateGoalIdMap(structuredGoalData: PopulatedGoalStructureContent): Map<string, PopulatedGoalStructureContentItem> {

		const map = new Map<string, PopulatedGoalStructureContentItem>();

		const traverse = (node: PopulatedGoalStructureContentItem | any) => {
			if (node.goalIds) {
				node.goalIds.forEach((goalId: string) => map.set(goalId, node));
			}

			if (node.items && node.items.length > 0) {
				node.items.forEach(traverse);
			}
		}

		structuredGoalData.items.forEach(traverse);
		return map;

	}

	getGoals() {
		const result: any = [];

		const traverse = (node: any) => {

			if (node.items && node.items.length > 0) {
				node.items.forEach(traverse);
			} else {
				if (!this.sourceId) {
					throw new Error('Source ID not set');
				}
				
				node = Goal.mapFromStaticGoalSourceData(node, this.sourceId);
				result.push(node);
			}
		}

		if (!this.content) {
			return [];
		}

		traverse(this.content);
		return result;
	}

	getTags() {
		const tagsSet = new Set();

		const traverse = (node: any) => {

			if (node.items && node.items.length > 0) {
				node.items.forEach(traverse);
			} else {
				if(node.meta?.tags?.length) {
					node.meta.tags.forEach((tag: any) => {
						tagsSet.add(tag);
					});
				}
			}
		}

		if (this.content) {
			traverse(this.content);
		}

		const tagsArray = Array.from(tagsSet);
		return tagsArray.sort();
	}

	getPopulatedStructure(searchResults: GoalSearchResult[]): PopulatedGoalStructureContent {

		// No skeleton available (provider returned null / not loaded): synthesize a
		// minimal one from the goals' own dotted paths so they are never dropped.
		if (!this.content) {
			if (!searchResults.length) {
				return new PopulatedGoalStructureContent(this.sourceId);
			}
			return this.buildSyntheticStructure(searchResults);
		}

		if (!searchResults.length) {
			// return only first node of the structure with general source info (and empty items array)
			const structure = new PopulatedGoalStructureContent(
				this.sourceId,
				this.content.name,
				this.content.version,
				this.content.copyright
			);

			return structure;
		}

		let structuredGoalData = this.getEmptyStructure();
		if (!structuredGoalData) {
			throw new Error('Empty structure not found');
		}

		let nodeMap = this.calculateNodePathMap(structuredGoalData);
		let goalIdMap = this.calculateGoalIdMap(structuredGoalData);
		const fallbackNodes = new Map<string, PopulatedGoalStructureContentItem>();

		searchResults.forEach((searchResult: GoalSearchResult) => {
			// A node that claims this goal outright wins: its curriculum numbers goals in a
			// flat sequence, so walking up the path could never find the right heading.
			const claimingNode = searchResult.goal.sourceId
				? goalIdMap.get(searchResult.goal.sourceId)
				: undefined;
			if (claimingNode) {
				claimingNode.items.push(searchResult);
				return;
			}

			// look for the most specific parent path in the structure, but move up the hierarchy if not found
			let parentPathParts = searchResult.goal.fullPath.split('.');
			let parentPath: string | null = null;

			while (parentPathParts.length > 0) {
				const currentPath = parentPathParts.join('.');
				if (nodeMap.has(currentPath)) {
					parentPath = currentPath;
					break;
				}
				parentPathParts.pop();
			}

			const parent = parentPath ? nodeMap.get(parentPath) : undefined;
			if (parent) {
				parent.items.push(searchResult);
				return;
			}

			// Nothing in the skeleton fits (a curriculum added after the last sync, a goal
			// without a resolvable path). It is still a selected goal, so rather than
			// pretending it is not there, group it under a synthetic root heading named
			// after its first path segment — the shape buildSyntheticStructure produces
			// when there is no skeleton at all. Consumers only render goals inside headings.
			const segment = searchResult.goal.fullPath.split('.')[0];
			let fallback = fallbackNodes.get(segment);
			if (!fallback) {
				fallback = new PopulatedGoalStructureContentItem(segment, segment, '', [], this.sourceId);
				fallbackNodes.set(segment, fallback);
				structuredGoalData.items.push(fallback);
			}
			fallback.items.push(searchResult);
		});

		this.restoreCurriculumOrder(structuredGoalData);

		const cleanedUpStructure = this.removeNodesWithoutChildren([ structuredGoalData ]);
		return cleanedUpStructure[0];

	}

	/**
	 * Put the goals a node claimed back into the order their curriculum lists them.
	 *
	 * They arrive sorted by path, which is close but cannot separate subgoals: every subgoal
	 * of a goal shares one path ('NL.003.x'), so their order falls back to however the API
	 * returned them. `goalIds` is written in curriculum order, so it says exactly where each
	 * goal belongs. Nodes without `goalIds` are left alone, as are nested headings, which
	 * keep the position the skeleton gave them.
	 */
	private restoreCurriculumOrder(node: PopulatedGoalStructureContent | PopulatedGoalStructureContentItem): void {

		const items = node.items as (GoalSearchResult | PopulatedGoalStructureContentItem)[];
		const headings = items.filter(
			(item): item is PopulatedGoalStructureContentItem => item instanceof PopulatedGoalStructureContentItem
		);

		const goalIds = (node as PopulatedGoalStructureContentItem).goalIds;
		if (goalIds && goalIds.length > 0) {
			const orderById = new Map(goalIds.map((goalId, index) => [goalId, index]));
			const goals = items.filter((item) => !(item instanceof PopulatedGoalStructureContentItem));

			// Anything the node did not name sorts last, keeping its relative order.
			const positionOf = (item: any) => orderById.get(item.goal?.sourceId) ?? Number.MAX_SAFE_INTEGER;
			goals.sort((a, b) => positionOf(a) - positionOf(b));

			node.items = [...headings, ...goals];
		}

		headings.forEach((heading) => this.restoreCurriculumOrder(heading));
	}

	/**
	 * Build a skeleton from the search results themselves, grouping each goal under
	 * ancestor nodes derived from its sourceParentPath (falling back to fullPath),
	 * with identifier segments as titles.
	 * Used when no structure content could be loaded for this source.
	 */
	private buildSyntheticStructure(searchResults: GoalSearchResult[]): PopulatedGoalStructureContent {

		const nodeMap = new Map<string, PopulatedGoalStructureContentItem>();
		const roots: PopulatedGoalStructureContentItem[] = [];

		const ensureNode = (path: string): PopulatedGoalStructureContentItem => {
			const existing = nodeMap.get(path);
			if (existing) {
				return existing;
			}
			const segments = path.split('.');
			const title = segments[segments.length - 1];
			const node = new PopulatedGoalStructureContentItem(path, title, '', [], this.sourceId);
			nodeMap.set(path, node);
			if (segments.length === 1) {
				roots.push(node);
			} else {
				const parent = ensureNode(segments.slice(0, -1).join('.'));
				parent.items.push(node);
			}
			return node;
		};

		searchResults.forEach((searchResult) => {
			// Use sourceParentPath directly rather than deriving it from fullPath: fullPath is a
			// plain concatenation (sourceParentPath + '.' + sourcePathId) with no unambiguous
			// boundary, so a pathId containing its own dots (e.g. 'PF1.1') would otherwise leave
			// a stray identifier segment attached to the parent path.
			const parentPath = searchResult.goal.sourceParentPath || searchResult.goal.fullPath;
			ensureNode(parentPath).items.push(searchResult);
		});

		return new PopulatedGoalStructureContent(this.sourceId, '', '', '', roots);
	}

	private withoutGoalNodes(structuredData: any) {
		return structuredData.filter((obj: any) => {
			const keep = obj.items && obj.items.length > 0;	// TODO might not be 100% reliable
			if(keep) {
				obj.items = this.withoutGoalNodes(obj.items);
			}
			return keep;
		});
	}

	private removeNodesWithoutChildren(data: any) {

		return data.filter((item: any) => {
			if (!item.items) {
				// Keep goals
				return true;
			}
			else if (item.items.length > 0) {
				// Recursively remove nodes without children in the children array
				item.items = this.removeNodesWithoutChildren(item.items);
				return item.items.length > 0;
				// return true;
			}
			// Exclude nodes without children from the result
			return false;
		});
	}

}
